<template>
	<app-page>
		<app-heading title="Reviews">
			<template v-slot:right>
				<app-button
					v-if="$ravello.can('system.post')"
					outlined
					small
					color="warning"
					icon="fa-download"
					@click="openSyncReviews()"
				>Sync Reviews</app-button>
				<app-button
					outlined
					small
					color="secondary"
					icon="fa-sync"
					@click="refresh = true"
				>Refresh</app-button>
			</template>
		</app-heading>
		<app-filter :value="hasFilters" @click="applyFilters()">
			<app-row no-padding>
				<app-cell cols="6" md="4">
					<input-text
						v-model="filters.external_confirmation_id"
						label="Confirmation Number"
						clearable
						dense
					></input-text>
				</app-cell>
				<app-cell cols="6" md="4">
					<input-select
						v-model="filters.integration"
						label="Integration"
						:items="integrations"
						clearable
						dense
					></input-select>
				</app-cell>
				<app-cell cols="6" md="4">
					<input-select
						v-model="filters.status"
						label="Status"
						:items="statuses"
						clearable
						dense
					></input-select>
				</app-cell>
			</app-row>
		</app-filter>
		<app-row>
			<app-cell>
				<app-data-table
					:items="reviews"
					:total="count"
					:refresh.sync="refresh"
					:headers="headers"
					:initial-state="tableState"
					:sort-by="'submitted'"
					:sort-desc="true"
					@update="fetchReviews"
				>
					<template v-slot:status="{ item }">
							<span v-if="item.status === 0" class="warning--text">Pending</span>
							<span v-if="item.status === 1" class="success--text">Success</span>
							<span v-if="item.status === 2" class="error--text">Error</span>
					</template>
					<template v-slot:last_action="{ item }">
							<span v-if="item.last_action === 'new'">New</span>
							<span v-if="item.last_action === 'reply'">Replied</span>
					</template>
					<template v-slot:actions="{ item }">
						<app-button
							color="secondary"
							:to="{ name: 'studeo-review', params: { review: item._id } }"
							icon="fa-history"
							table
						>
							History
						</app-button>
						<app-button-confirm
							color="secondary"
							icon="fa-sync"
							v-if="$ravello.can('studeo.post')"
							table
							:disabled="item.status !== 2"
							label-confirm="Sync"
							:description="`Are you sure you want to retry the synchronization of the review to Studeo `
								+ `for <b>${item.external_confirmation_id}</b>?`"
							title="Retry Synchronization"
							@click="retry(item._id)"
						>
							Retry
						</app-button-confirm>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>
		<app-dialog
			v-model="syncReviewsDialog.show"
			max-width="320"
			title="Sync Reviews"
		>
			<app-dialog-body>
				<div>
					<input-select
						v-model="syncReviewsDialog.integration"
						:items="eventsIntegrations"
						placeholder="Integration"
					/>
				</div>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					flat="flat"
					color="warning"
					:disabled="!syncReviewsDialog.integration"
					@click="syncReviews"
				>Confirm</app-button>
				<app-button
					flat="flat"
					@click="syncReviewsDialog.show = false"
				>Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</app-page>
</template>

<script>
import { filters, tableState } from '@/mixins'
import { mapSelectOptions } from '@/utils/maps'

export default {
	mixins: [filters, tableState],
	data() {
		return {
			reviews: [],
			count: -1,
			refresh: false,
			syncReviewsDialog: {
				show: false,
				integration: undefined,
			},
			headers: [{
				text: 'Status',
				value: 'status',
				sortable: true,
			}, {
				text: 'Integration',
				value: 'integration.name',
				sortable: false,
			}, {
				text: 'Reservation ID',
				value: 'external_confirmation_id',
				sortable: true,
			}, {
				text: 'Comment',
				value: 'comment',
				sortable: true,
			}, {
				text: 'Score',
				value: 'score',
				sortable: true,
			}, {
				text: 'Submitted On',
				value: 'submitted',
				format: 'datetime',
				sortable: true,
			}, {
				text: 'State',
				value: 'last_action',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
			defaultTableSort: '-submitted',
			integrations: [],
			eventsIntegrations: [{
				text: 'All',
				value: 'v2.studeo.sync.reviews',
			}, {
				text: 'Airbnb',
				value: 'v2.airbnb.sync.reviews',
			}, {
				text: 'Booking.com',
				value: 'v2.bookingcom.sync.reviews',
			}, {
				text: 'HomeAway/VRBO',
				value: 'v2.homeaway.sync.reviews',
			}],
			filters: {
				external_confirmation_id: '',
				integration: null,
				status: null,
			},
			statuses: [{
				text: 'Pending',
				value: 0,
			}, {
				text: 'Success',
				value: 1,
			}, {
				text: 'Failed',
				value: 2,
			}],
		}
	},
	async created() {
		this.integrations = mapSelectOptions(await this.$ravello.request()
			.v(2)
			.to('integrations')
			.field(['name'])
			.limit(100)
			.get())
	},
	methods: {
		async fetchReviews(pagination) {
			this.updateTableState(pagination)
			const request = this.$ravello.graphql('reviews')
				.fromPagination(pagination)
				.include(this.$ravello.graphql('integration').field('name'))
				.fields(['_id', 'comment', 'external_confirmation_id', 'score', 'status', 'submitted', 'last_action'])

			if (this.appliedFilters.external_confirmation_id) {
				request.filter('external_confirmation_id', this.appliedFilters.external_confirmation_id)
			}
			if (this.appliedFilters.integration) {
				request.filter('integration', this.appliedFilters.integration)
			}
			if (this.appliedFilters.status) {
				request.filter('status', this.appliedFilters.status)
			}

			if (this.count === -1) {
				request.count('count')
			}

			try {
				this.reviews = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async retry(id) {
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.v(2)
					.to('reviews')
					.to(id)
					.to('retry')
					.create()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},
		async syncReviews() {
			this.syncReviewsDialog.show = false
			this.$store.dispatch('app/startLoading')

			try {
				await this.$ravello.request()
					.to('system')
					.to('events')
					.to('trigger')
					.create({
						event: this.syncReviewsDialog.integration,
						data: {},
					})
				this.$notifications.pushAlert('success', 'Reviews synchronization will start shortly')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}

			this.$store.dispatch('app/stopLoading')
		},

		openSyncReviews() {
			this.syncReviewsDialog.integration = undefined
			this.syncReviewsDialog.show = true
		},
	},
}
</script>
